import type { ReactNode } from 'react'
import { useSectionProgress } from '@/hooks/useSectionProgress'
import { cn } from '@/lib/cn'

export interface RailStep {
  key: string
  /** Short marker shown in the node, e.g. "01" or "T3". */
  badge: string
  content: ReactNode
}

/**
 * A vertical timeline whose connecting line draws itself as the reader scrolls
 * through the section, lighting each node as it is reached.
 *
 * The list is an ordinary ordered list underneath: the rail is decoration on
 * top of correct semantics, and under reduced motion it simply renders fully
 * drawn.
 */
export function ScrollRail({ steps, className }: { steps: RailStep[]; className?: string }) {
  const { ref, progress } = useSectionProgress<HTMLOListElement>()

  return (
    <ol ref={ref} className={cn('relative grid gap-5 pl-14 sm:pl-16', className)}>
      {/* Track */}
      <span
        aria-hidden="true"
        className="absolute bottom-6 left-[19px] top-6 w-[2px] rounded-full bg-line sm:left-[23px]"
      />
      {/* Drawn portion */}
      <span
        aria-hidden="true"
        className="absolute left-[19px] top-6 w-[2px] origin-top rounded-full bg-gradient-to-b from-primary via-violet to-computes sm:left-[23px]"
        style={{ height: `calc((100% - 3rem) * ${progress})` }}
      />

      {steps.map((step, index) => {
        // A node lights up once the drawn line has reached it.
        const threshold = steps.length > 1 ? index / (steps.length - 1) : 0
        const reached = progress >= threshold * 0.92

        return (
          <li key={step.key} className="relative">
            <span
              aria-hidden="true"
              className={cn(
                'absolute -left-14 top-4 flex h-10 w-10 items-center justify-center rounded-full border font-mono text-[10.5px] transition-all duration-500 ease-brand sm:-left-16',
                reached
                  ? 'border-primary/35 bg-white text-primary shadow-card'
                  : 'border-line bg-white/70 text-muted-soft',
              )}
            >
              {step.badge}
              {reached ? (
                <span className="anim-pulse-ring absolute inset-0 rounded-full bg-primary/20" />
              ) : null}
            </span>

            <div
              className={cn(
                'motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-brand',
                reached ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-45',
              )}
            >
              {step.content}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
